import moment from "moment";
import { OmitField } from "src/app/@shared/decorators/api-field.decorator";
import { ModelBase } from "../../abstract/model-base.model";
import { Account } from "../../accounting/account.model";
import { Bank } from "../config/bank.model";
import { BankAccount } from "../bank-accounts/bank-account.model";
import { Currency } from "../../core/currency.model";
import { LoanDeferralMode } from "./loan-deferral-mode.model";
import { LoanEvent } from "./loan-event.model";
import { LoanInstalment } from "./loan-instalment.model";
import { LoanInsuranceMode } from "./loan-insurance-mode.model";
import { LoanPeriodicity } from "./loan-periodicity.model";
import { LoanRepaymentProfile } from "./loan-repayment-profile.model";
import { LoanState } from "./loan-state.model";

export class Loan extends ModelBase {

    static override CLASS_NAME: string = 'Loan';

    private _arrangementFees!: number;
    private _bank!: Bank;
    private _bankAccount!: BankAccount;
    private _capital!: number;
    private _capitalAccount!: Account;
    private _capitalOrg!: number;
    private _capitalRepaid!: number;
    private _capitalRepaidRatio!: number;
    private _currency!: Currency;
    private _deferralExtendsDuration!: boolean;
    private _deferredMode!: LoanDeferralMode;
    private _deferredMonths!: number;
    private _duration!: number;
    private _endDate!: moment.Moment;
    private _events!: LoanEvent[];
    private _exchangeRate!: number;
    private _firstInstalmentDate!: moment.Moment;
    private _instalments: LoanInstalment[] = [];
    private _insuranceAccount!: Account;
    private _insuranceMode!: LoanInsuranceMode;
    private _insurancePaid!: number;
    private _insuranceRate!: number;
    private _interestAccount!: Account;
    private _interestPaid!: number;
    private _label!: string;
    private _loanDate!: moment.Moment;
    private _manualExchangeRate!: boolean;
    private _nextInstalmentAmount!: number;
    private _nextInstalmentDate!: moment.Moment;
    private _outstandingAmount!: number;
    private _penaltyAccount!: Account;
    private _periodicity!: LoanPeriodicity;
    private _rate!: number;
    private _reference!: string;
    private _repaymentProfile!: LoanRepaymentProfile;
    private _state!: LoanState;
    private _tofAccount!: Account;
    private _tofRate!: number;
    private _totalCreditCost!: number;

    public get arrangementFees(): number {
        return this._arrangementFees;
    }

    public set arrangementFees(value: any) {
        this._setValue('_arrangementFees', value, 'float');
    }

    public get bank(): Bank {
        return this._bank;
    }

    public set bank(value: any) {
        this._setValue('_bank', value, 'Bank');
    }

    public get bankAccount(): BankAccount {
        return this._bankAccount;
    }

    public set bankAccount(value: any) {
        this._setValue('_bankAccount', value, 'BankAccount');
    }

    public get capital(): number {
        return this._capital;
    }

    public set capital(value: any) {
        this._setValue('_capital', value, 'float');
    }

    public get capitalAccount(): Account {
        return this._capitalAccount;
    }

    public set capitalAccount(value: any) {
        this._setValue('_capitalAccount', value, 'Account');
    }

    @OmitField()
    public get capitalOrg(): number {
        return this._capitalOrg;
    }

    public set capitalOrg(value: any) {
        this._setValue('_capitalOrg', value, 'float');
    }

    @OmitField()
    public get capitalRepaid(): number {
        return this._capitalRepaid;
    }

    public set capitalRepaid(value: any) {
        this._setValue('_capitalRepaid', value, 'float');
    }

    @OmitField()
    public get capitalRepaidRatio(): number {
        return this._capitalRepaidRatio;
    }

    public set capitalRepaidRatio(value: any) {
        this._setValue('_capitalRepaidRatio', value, 'float');
    }

    public get currency(): Currency {
        return this._currency;
    }

    public set currency(value: any) {
        this._setValue('_currency', value, 'Currency');
    }

    public get deferralExtendsDuration(): boolean {
        return this._deferralExtendsDuration;
    }

    public set deferralExtendsDuration(value: any) {
        this._setValue('_deferralExtendsDuration', value, 'boolean');
    }

    public get deferredMode(): LoanDeferralMode {
        return this._deferredMode;
    }

    public set deferredMode(value: any) {
        this._setValue('_deferredMode', value, 'LoanDeferralMode');
    }

    public get deferredMonths(): number {
        return this._deferredMonths;
    }

    public set deferredMonths(value: any) {
        this._setValue('_deferredMonths', value, 'integer');
    }

    public get duration(): number {
        return this._duration;
    }

    public set duration(value: any) {
        this._setValue('_duration', value, 'integer');
    }

    @OmitField()
    public get endDate(): moment.Moment {
        return this._endDate;
    }

    public set endDate(value: any) {
        this._setValue('_endDate', value, 'date');
    }

    @OmitField()
    public get events(): LoanEvent[] {
        return this._events;
    }

    public set events(value: any) {
        this._setValue('_events', value ?? [], 'LoanEvent');
    }

    @OmitField()
    public get exchangeRate(): number {
        return this._exchangeRate;
    }

    public set exchangeRate(value: any) {
        this._setValue('_exchangeRate', value, 'float');
    }

    public get firstInstalmentDate(): moment.Moment {
        return this._firstInstalmentDate;
    }

    public set firstInstalmentDate(value: any) {
        this._setValue('_firstInstalmentDate', value, 'date');
    }

    @OmitField()
    public get instalments(): LoanInstalment[] {
        return this._instalments;
    }

    public set instalments(value: any) {
        this._setValue('_instalments', value ?? [], 'LoanInstalment');
    }

    public get insuranceAccount(): Account {
        return this._insuranceAccount;
    }

    public set insuranceAccount(value: any) {
        this._setValue('_insuranceAccount', value, 'Account');
    }

    public get insuranceMode(): LoanInsuranceMode {
        return this._insuranceMode;
    }

    public set insuranceMode(value: any) {
        this._setValue('_insuranceMode', value, 'LoanInsuranceMode');
    }

    @OmitField()
    public get insurancePaid(): number {
        return this._insurancePaid;
    }

    public set insurancePaid(value: any) {
        this._setValue('_insurancePaid', value, 'float');
    }

    public get insuranceRate(): number {
        return this._insuranceRate;
    }

    public set insuranceRate(value: any) {
        this._setValue('_insuranceRate', value, 'float');
    }

    public get interestAccount(): Account {
        return this._interestAccount;
    }

    public set interestAccount(value: any) {
        this._setValue('_interestAccount', value, 'Account');
    }

    @OmitField()
    public get interestPaid(): number {
        return this._interestPaid;
    }

    public set interestPaid(value: any) {
        this._setValue('_interestPaid', value, 'float');
    }

    public get label(): string {
        return this._label;
    }

    public set label(value: any) {
        this._setValue('_label', value, 'string');
    }

    public get loanDate(): moment.Moment {
        return this._loanDate;
    }

    public set loanDate(value: any) {
        this._setValue('_loanDate', value, 'date');
    }

    @OmitField()
    public get manualExchangeRate(): boolean {
        return this._manualExchangeRate;
    }

    public set manualExchangeRate(value: any) {
        this._setValue('_manualExchangeRate', value, 'boolean');
    }

    @OmitField()
    public get nextInstalmentAmount(): number {
        return this._nextInstalmentAmount;
    }

    public set nextInstalmentAmount(value: any) {
        this._setValue('_nextInstalmentAmount', value, 'float');
    }

    @OmitField()
    public get nextInstalmentDate(): moment.Moment {
        return this._nextInstalmentDate;
    }

    public set nextInstalmentDate(value: any) {
        this._setValue('_nextInstalmentDate', value, 'date');
    }

    @OmitField()
    public get outstandingAmount(): number {
        return this._outstandingAmount;
    }

    public set outstandingAmount(value: any) {
        this._setValue('_outstandingAmount', value, 'float');
    }

    public get penaltyAccount(): Account {
        return this._penaltyAccount;
    }

    public set penaltyAccount(value: any) {
        this._setValue('_penaltyAccount', value, 'Account');
    }

    public get periodicity(): LoanPeriodicity {
        return this._periodicity;
    }

    public set periodicity(value: any) {
        this._setValue('_periodicity', value, 'LoanPeriodicity');
    }

    public get rate(): number {
        return this._rate;
    }

    public set rate(value: any) {
        this._setValue('_rate', value, 'float');
    }

    public get reference(): string {
        return this._reference;
    }

    public set reference(value: any) {
        this._setValue('_reference', value, 'string');
    }

    public get repaymentProfile(): LoanRepaymentProfile {
        return this._repaymentProfile;
    }

    public set repaymentProfile(value: any) {
        this._setValue('_repaymentProfile', value, 'LoanRepaymentProfile');
    }

    @OmitField()
    public get state(): LoanState {
        return this._state;
    }

    public set state(value: any) {
        this._setValue('_state', value, 'LoanState');
    }

    public get tofAccount(): Account {
        return this._tofAccount;
    }

    public set tofAccount(value: any) {
        this._setValue('_tofAccount', value, 'Account');
    }

    public get tofRate(): number {
        return this._tofRate;
    }

    public set tofRate(value: any) {
        this._setValue('_tofRate', value, 'float');
    }

    @OmitField()
    public get totalCreditCost(): number {
        return this._totalCreditCost;
    }

    public set totalCreditCost(value: any) {
        this._setValue('_totalCreditCost', value, 'float');
    }

}
